// ./src/utils/api/EventTimeSlotService.ts
//
// Event time slots — the materialized occurrences of a recurring event, and the
// hand-authored sub-events of an agenda event. CRUD plus `generate`, which asks
// the server to (re)materialize a recurring event's occurrences from its saved
// rule (event.settings.recurrence). Field names mirror the eventsmagic-api
// EventTimeSlot SDL.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

const SLOT_FIELDS = `
  id
  eventId
  accountId
  name
  description
  descriptionFormat
  speakerGuestIds
  track
  startsAt
  endsAt
  isAllDay
  venueId
  mapElementId
  eventArtistIds
  onlineMeetingUrl
  capacity
  rsvpDeadline
  doorsOpenMinutes
  doorsOpenAt
  rsvpEnabled
  isGenerated
  isOverridden
  occurrenceKey
  dayOffset
  startTime
  endTime
  canceledAt
  cancelReason
  status
  createdAt
  updatedAt
`;

const SLOT_LIST_QUERY = gql`
  query EventTimeSlotList($filter: EventTimeSlotFilter, $params: PaginationAndSorting) {
    eventTimeSlotList(filter: $filter, params: $params) {
      code
      data { ${SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SLOT_CREATE_MUTATION = gql`
  mutation EventTimeSlotCreate($params: EventTimeSlotInput) {
    eventTimeSlotCreate(params: $params) {
      code
      data { ${SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SLOT_UPDATE_MUTATION = gql`
  mutation EventTimeSlotUpdate($where: EventTimeSlotWhereInput, $params: EventTimeSlotInput) {
    eventTimeSlotUpdate(where: $where, params: $params) {
      code
      data { ${SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SLOT_REMOVE_MUTATION = gql`
  mutation EventTimeSlotRemove($where: EventTimeSlotWhereInput) {
    eventTimeSlotRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const SCHEDULE_GENERATE_MUTATION = gql`
  mutation EventScheduleGenerate($params: EventScheduleGenerateInput) {
    eventScheduleGenerate(params: $params) {
      code
      data { ${SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #199 — cancelling ONE date: what it would do (read-only), the cancel itself, and a retry of its failed refunds.
const DATE_CANCEL_FIELDS = `
  eventTimeSlotId
  eventId
  dateLabel
  canceledAt
  cancelReason
  recipients
  unreachable
  attendees
  crew
  volunteers
  artists
  refundableOrders
  refundableOrderAmount
  refundableTotal
  currencyCode
  canRefund
  refundsQueued
  refundsDone
  refundsFailed
  refundsSkipped
  refundedSoFar
`;

const DATE_CANCEL_STATUS_QUERY = gql`
  query EventTimeSlotCancelStatus($params: EventTimeSlotCancelStatusInput) {
    eventTimeSlotCancelStatus(params: $params) {
      code
      data { ${DATE_CANCEL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const DATE_CANCEL_MUTATION = gql`
  mutation EventTimeSlotCancel($params: EventTimeSlotCancelInput) {
    eventTimeSlotCancel(params: $params) {
      code
      data { ${DATE_CANCEL_FIELDS} notified notifyFailed }
      errors { name errors warnings }
    }
  }
`;

const DATE_CANCEL_RETRY_MUTATION = gql`
  mutation EventTimeSlotCancelRetryRefunds($params: EventTimeSlotCancelStatusInput) {
    eventTimeSlotCancelRetryRefunds(params: $params) {
      code
      data { ${DATE_CANCEL_FIELDS} requeued }
      errors { name errors warnings }
    }
  }
`;

/**
 * What cancelling one date would do — or, once cancelled, how its refunds are getting on (`eventTimeSlotCancelStatus`).
 * ⚠ The per-audience counts overlap (merged people) — never add them up; `recipients` is the number of emails.
 */
export interface DateCancelStatus {
  eventTimeSlotId?: string | null;
  eventId?: string | null;
  /** The date as the email names it ("Tue, Oct 28 · 6:30 PM"). */
  dateLabel?: string | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
  recipients?: number | null;
  unreachable?: number | null;
  attendees?: number | null;
  crew?: number | null;
  volunteers?: number | null;
  artists?: number | null;
  refundableOrders?: number | null;
  refundableOrderAmount?: number | null;
  refundableTotal?: number | null;
  currencyCode?: string | null;
  /** May THIS person send the money back (tickets.manage)? False → no refund box. */
  canRefund?: boolean | null;
  refundsQueued?: number | null;
  refundsDone?: number | null;
  refundsFailed?: number | null;
  refundsSkipped?: number | null;
  refundedSoFar?: number | null;
  /** The cancel only: how many were emailed, and how many the sender refused. */
  notified?: number | null;
  notifyFailed?: number | null;
  /** The retry only. */
  requeued?: number | null;
}

export interface EventTimeSlot {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  name?: string | null;
  description?: string | null;
  /** How `description` is authored: richtext | markdown | html | plain (POLICY_CONTENT_FORMAT). */
  descriptionFormat?: string | null;
  /** The team members (event_guest ids) shown on this session, in order — only people still on the event's team. */
  speakerGuestIds?: string[] | null;
  track?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  venueId?: string | null;
  mapElementId?: string | null;
  /** Optional link → the event artist booking (performer) at this slot. */
  /** The booked acts playing this session, in order (#70). Send [] to clear; omit to leave them. */
  eventArtistIds?: string[] | null;
  onlineMeetingUrl?: string | null;
  capacity?: number | null;
  rsvpDeadline?: string | null;
  /** #41 — minutes before THIS date starts that check-in opens; null = use the event's. 0 = at the start. */
  doorsOpenMinutes?: number | null;
  /** #41 — when the door opens, RESOLVED by the server (date → event → default). Read-only. */
  doorsOpenAt?: string | null;
  rsvpEnabled?: boolean | null;
  isGenerated?: boolean | null;
  isOverridden?: boolean | null;
  occurrenceKey?: string | null;
  dayOffset?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EventTimeSlotInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  descriptionFormat?: string | null;
  /** The session's people, in order. Omit to leave them; [] clears them. */
  speakerGuestIds?: string[] | null;
  track?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  dayOffset?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  isAllDay?: boolean | null;
  venueId?: string | null;
  mapElementId?: string | null;
  /** The booked acts playing this session, in order (#70). Send [] to clear; omit to leave them. */
  eventArtistIds?: string[] | null;
  onlineMeetingUrl?: string | null;
  capacity?: number | null;
  rsvpDeadline?: string | null;
  /** #41 — this date's own door; null = inherit the event's. */
  doorsOpenMinutes?: number | null;
  rsvpEnabled?: boolean | null;
  canceledAt?: string | null;
  cancelReason?: string | null;
}

export interface EventTimeSlotFilter {
  id?: string[];
  eventId?: string[];
  venueId?: string[];
  mapElementId?: string[];
}

export class EventTimeSlotService {
  static async list(filter?: EventTimeSlotFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(SLOT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: EventTimeSlotFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<EventTimeSlot>(EventTimeSlotService.list, filter, sortBy, opts);
  }

  static async create(params: EventTimeSlotInput): Promise<OpResult> {
    return ApiService.executeMutation(SLOT_CREATE_MUTATION, { params });
  }

  static async update(id: string, params: EventTimeSlotInput): Promise<OpResult> {
    return ApiService.executeMutation(SLOT_UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(SLOT_REMOVE_MUTATION, { where: { id } });
  }

  /** Ask the server to (re)generate a recurring event's occurrences from its rule. */
  static async generate(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(SCHEDULE_GENERATE_MUTATION, { params: { eventId } });
  }

  /** #199 — what cancelling this date would do (read on every open — never cached), or how its refunds are going. */
  static async cancelStatus(eventTimeSlotId: string): Promise<OpResult> {
    return ApiService.executeQuery(DATE_CANCEL_STATUS_QUERY, { params: { eventTimeSlotId } });
  }

  /**
   * #199 — cancel ONE date: everyone booked on it is told. ⚠ `refundEveryone` is sent as a real boolean; the server
   * treats anything but `true` as "do not move money".
   */
  static async cancel(params: { eventTimeSlotId: string; reason: string | null; refundEveryone: boolean }): Promise<OpResult> {
    return ApiService.executeMutation(DATE_CANCEL_MUTATION, { params });
  }

  /** #199 — put a cancelled date's failed refunds back in the queue. */
  static async retryCancelRefunds(eventTimeSlotId: string): Promise<OpResult> {
    return ApiService.executeMutation(DATE_CANCEL_RETRY_MUTATION, { params: { eventTimeSlotId } });
  }
}
